/**
 * InvoiceInbox — the v4 confirm queue ("is this your Coke?").
 *
 * Shows invoice lines the matching cascade couldn't place with confidence.
 * Each answer is learned once, forever — the queue is heavy in week one and
 * near-zero by week four. On the home page (variant="home") it renders
 * nothing when the queue is empty; on the Costs page it shows a quiet
 * all-clear instead.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Inbox, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { pageCache } from '../utils/pageCache';
import { fmtPct } from '../utils/formatters';

interface QueueLine {
  line_id: string;
  description: string;
  pack_size: string;
  supplier_name: string;
  invoice_date: string;
  quantity: number;
  line_total: number;
  suggestion: {
    catalog_item: string | null;
    confidence: number;
    units_per_pack: number;
    direct_resale: boolean;
  } | null;
}

interface Draft {
  catalog_item: string;
  units_per_pack: string;
  direct_resale: boolean;
}

export default function InvoiceInbox({ variant = 'home' }: { variant?: 'home' | 'upload' }) {
  const [queue, setQueue] = useState<QueueLine[] | null>(null);
  const [catalog, setCatalog] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    axios.get('/api/invoices/queue')
      .then((res) => {
        const lines: QueueLine[] = res.data.queue || [];
        setQueue(lines);
        if (lines.length > 0) {
          axios.get('/api/products?period=quarter')
            .then((r) => setCatalog((r.data.products || []).map((p: any) => p.name).sort()))
            .catch(() => setCatalog([]));
        }
      })
      .catch(() => setQueue([]));
  }, []);

  const draftFor = (line: QueueLine): Draft =>
    drafts[line.line_id] ?? {
      catalog_item: line.suggestion?.catalog_item ?? '',
      units_per_pack: String(line.suggestion?.units_per_pack || 1),
      direct_resale: line.suggestion?.direct_resale ?? true,
    };

  const setDraft = (id: string, patch: Partial<Draft>, line: QueueLine) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...draftFor(line), ...prev[id], ...patch } }));

  const answer = async (line: QueueLine, notSold: boolean) => {
    const d = draftFor(line);
    if (!notSold && !d.catalog_item) {
      toast.error('Pick the product this matches, or mark it as not something you sell');
      return;
    }
    setBusy(line.line_id);
    try {
      await axios.post('/api/invoices/confirm', {
        line_id: line.line_id,
        catalog_item: notSold ? null : d.catalog_item,
        units_per_pack: notSold ? 1 : Math.max(1, Number(d.units_per_pack) || 1),
        direct_resale: notSold ? false : d.direct_resale,
      });
      setQueue((prev) => (prev || []).filter((l) => l.line_id !== line.line_id));
      pageCache.clear('/api/'); // costs changed — every page recomputes
      toast.success(notSold ? 'Got it — we won\'t ask about that again' : `Matched to ${d.catalog_item} — remembered forever`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Couldn\'t save that answer');
    } finally {
      setBusy(null);
    }
  };

  if (queue === null) return null; // still fetching — never show a loader for this
  if (queue.length === 0) {
    if (variant === 'home') return null;
    return (
      <p className="font-sans text-sm text-[#6B6670]">
        No invoice lines waiting on you — everything matched automatically.
      </p>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className={`bg-white border border-[#E6E2DE] rounded-[14px] shadow-card overflow-hidden ${variant === 'home' ? 'mb-8' : ''}`}
    >
      <div className="px-6 py-4 border-b border-[#E6E2DE] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center">
          <Inbox size={18} strokeWidth={1.5} className="text-[#4A2D5E]" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-lg text-[#2D2A2E]">
            {queue.length} invoice {queue.length === 1 ? 'line needs' : 'lines need'} a look
          </h3>
          <p className="font-sans text-xs text-[#6B6670]">
            Answer once and ClearPath remembers forever — this list shrinks fast.
          </p>
        </div>
      </div>
      <div className="divide-y divide-[#E6E2DE]">
        {queue.slice(0, 8).map((line) => {
          const d = draftFor(line);
          return (
            <div key={line.line_id} className="px-6 py-4">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="font-mono text-sm text-[#2D2A2E]">{line.description}</span>
                <span className="font-sans text-xs text-[#6B6670]">
                  {line.pack_size} · {line.supplier_name} · {line.invoice_date}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select
                  value={d.catalog_item}
                  onChange={(e) => setDraft(line.line_id, { catalog_item: e.target.value }, line)}
                  className="border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 px-2 font-sans text-sm text-[#2D2A2E] bg-white max-w-[220px]"
                >
                  <option value="">Which product is this?</option>
                  {d.catalog_item && !catalog.includes(d.catalog_item) && (
                    <option value={d.catalog_item}>{d.catalog_item}</option>
                  )}
                  {catalog.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5 font-sans text-xs text-[#6B6670]">
                  <input
                    type="number"
                    min={1}
                    value={d.units_per_pack}
                    onChange={(e) => setDraft(line.line_id, { units_per_pack: e.target.value }, line)}
                    className="border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-9 w-16 px-2 font-sans text-sm text-[#2D2A2E] tabular-nums"
                  />
                  units per pack
                </label>
                <button
                  onClick={() => answer(line, false)}
                  disabled={busy === line.line_id}
                  className="inline-flex items-center gap-1.5 bg-[#4A2D5E] text-white hover:bg-[#3D254D] rounded-[8px] h-9 px-3 font-sans font-medium text-sm transition-colors disabled:opacity-50"
                >
                  <Check size={15} strokeWidth={2} /> Confirm
                </button>
                <button
                  onClick={() => answer(line, true)}
                  disabled={busy === line.line_id}
                  className="inline-flex items-center gap-1.5 bg-white border border-[#E6E2DE] text-[#6B6670] hover:text-[#2D2A2E] hover:bg-[#FAF9F7] rounded-[8px] h-9 px-3 font-sans text-sm transition-colors disabled:opacity-50"
                >
                  <X size={15} strokeWidth={2} /> Not something I sell
                </button>
              </div>
              {line.suggestion?.catalog_item && (
                <p className="mt-2 font-sans text-xs text-[#6B6670]">
                  Best guess: {line.suggestion.catalog_item}
                  {' '}({fmtPct(Math.round((line.suggestion.confidence || 0) * 100))} sure)
                </p>
              )}
            </div>
          );
        })}
      </div>
      {queue.length > 8 && (
        <div className="px-6 py-3 border-t border-[#E6E2DE] font-sans text-xs text-[#6B6670]">
          +{queue.length - 8} more after these
        </div>
      )}
    </motion.div>
  );
}
